export interface UrgentPlaylistLike {
  urgentMode?: string | null;
  urgentActiveFromUtc?: string | null;
  urgentActiveUntilUtc?: string | null;
  urgentInjectEveryNItems?: number | null;
  priority?: number | null;
}

export function isUrgentWindowActive(
  playlist: UrgentPlaylistLike,
  now: Date,
): boolean {
  if (!playlist?.urgentActiveUntilUtc) {
    return false;
  }

  const from = playlist.urgentActiveFromUtc
    ? new Date(playlist.urgentActiveFromUtc)
    : null;
  const until = playlist.urgentActiveUntilUtc
    ? new Date(playlist.urgentActiveUntilUtc)
    : null;

  if (from && Number.isNaN(from.getTime())) {
    return false;
  }

  if (until && Number.isNaN(until.getTime())) {
    return false;
  }

  if (from && now < from) {
    return false;
  }

  if (until && now > until) {
    return false;
  }

  return true;
}

export function isUrgentInjectPlaylistActive(
  playlist: UrgentPlaylistLike,
  now: Date,
): boolean {
  return (
    playlist?.urgentMode === "UrgentInject" &&
    isUrgentWindowActive(playlist, now)
  );
}

export function splitUrgentInjectPlaylists<T extends UrgentPlaylistLike>(
  playlists: T[],
  now: Date,
): { regularPlaylists: T[]; injectPlaylists: T[] } {
  const injectPlaylists: T[] = [];
  const regularPlaylists: T[] = [];

  for (const playlist of playlists) {
    if (isUrgentInjectPlaylistActive(playlist, now)) {
      injectPlaylists.push(playlist);
    } else {
      regularPlaylists.push(playlist);
    }
  }

  return { regularPlaylists, injectPlaylists };
}

export function sortByPriorityDesc<T extends UrgentPlaylistLike>(
  playlists: T[],
): T[] {
  return [...playlists].sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

export function getUrgentInjectCadence<T extends UrgentPlaylistLike>(
  injectPlaylists: T[],
  defaultCadence = 2,
): number {
  if (!injectPlaylists.length) {
    return defaultCadence;
  }

  const configuredCadence = injectPlaylists.map(
    (playlist) => playlist.urgentInjectEveryNItems || defaultCadence,
  );

  return Math.max(1, Math.min(...configuredCadence));
}

export function mergeInjectItems<T>(
  regularItems: T[],
  injectItems: T[],
  cadence: number,
): T[] {
  if (injectItems.length === 0) {
    return regularItems;
  }

  if (regularItems.length === 0) {
    return injectItems;
  }

  const merged: T[] = [];
  let regularCounter = 0;
  let injectIndex = 0;

  // Lead with an inject item so urgent content becomes visible immediately.
  merged.push(injectItems[injectIndex]);
  injectIndex++;

  for (const regularItem of regularItems) {
    merged.push(regularItem);
    regularCounter++;

    if (regularCounter >= cadence) {
      merged.push(injectItems[injectIndex % injectItems.length]);
      injectIndex++;
      regularCounter = 0;
    }
  }

  const unseenInjectStart = Math.min(injectIndex, injectItems.length);
  if (unseenInjectStart < injectItems.length) {
    merged.push(...injectItems.slice(unseenInjectStart));
  }

  return merged;
}
